import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const W = 420;

// Clinic CPT master (Settings → CPT codes), shown in a fixed popover so it never widens the form.
export function CptPicker({ cid, anchor, title, onPick, onClose, onClaim, testid = "cfv-addcpt" }) {
  const [q, setQ] = useState(""), [pos, setPos] = useState(null);
  const ref = useRef(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ["cpt-master", cid], enabled: !!cid, staleTime: 300_000,
    queryFn: async () => (await api.get("/api/edi/data-migration/config/cpt_codes", { params: { client_id: cid } })).data?.records || [],
  });
  useLayoutEffect(() => {
    const place = () => {
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8));
      const below = window.innerHeight - r.bottom > 260;
      setPos(below ? { top: r.bottom + 6, left } : { bottom: window.innerHeight - r.top + 6, left });
    };
    place();
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [anchor]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && !anchor?.contains(e.target)) onClose(); };
    const k = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("mousedown", h); document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [anchor, onClose]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return [...data].filter((r) => !t || String(r.code).toLowerCase().includes(t) || String(r.description || "").toLowerCase().includes(t))
      .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
  }, [data, q]);
  if (!pos) return null;
  return createPortal(
    <div ref={ref} className="cfv-adddx-pop" style={{ position: "fixed", ...pos, width: W, zIndex: 10050 }} data-testid={`${testid}-pop`}>
      <div className="cfv-adddx-search"><Search size={15} className="cfv-adddx-mag" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="CPT code or description" data-testid={`${testid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && shown[0]) onPick(shown[0]); }} /></div>
      <div className="cfv-adddx-list" style={{ maxHeight: 280, overflowY: "auto" }}>
        <div className="cfv-adddx-sec">{title || "Clinic CPT list · Settings"}</div>
        {isLoading ? <div className="cfv-adddx-empty">Loading…</div>
          : !data.length ? <div className="cfv-adddx-empty" data-testid={`${testid}-empty`}>This clinic has no CPT codes yet. Add them in Settings → CPT codes.</div>
          : !shown.length ? <div className="cfv-adddx-empty" data-testid={`${testid}-nomatch`}>No CPT on the clinic list matches “{q}”.</div>
          : shown.map((r) => (
            <div key={r.id || r.code} role="option" aria-selected="false" className="cfv-adddx-opt" onClick={() => onPick(r)} data-testid={`${testid}-opt-${r.code}`}>
              <div className="cfv-adddx-optmain"><span className="cfv-adddx-code">{r.code}</span><span className="cfv-adddx-desc">{r.description || ""}</span>
                {onClaim?.(r.code) ? <span className="cfv-adddx-onclaim">On claim</span> : null}</div>
            </div>
          ))}
      </div>
    </div>, document.body);
}

export function AddCptLine({ cid, onAdd, onClaim }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <button type="button" className="cfv-adddx-btn" onClick={(e) => setAnchor(anchor ? null : e.currentTarget)} aria-expanded={!!anchor} data-testid="cfv-addcpt-btn">+ Add CPT</button>
      {anchor ? <CptPicker cid={cid} anchor={anchor} onClaim={onClaim} onClose={() => setAnchor(null)} onPick={(r) => { onAdd(r); setAnchor(null); }} /> : null}
    </>
  );
}
